/**
 * EsfuerzoHost — la tarjeta «¿Cuánto te llevó?» (ver src/lib/esfuerzo.js). Un toque en una de las horas y listo.
 * Es obligatoria: no se puede cerrar sin cargar las horas. No tapa el trabajo (es una hoja abajo, sin fondo que bloquee) y lo que quede pendiente
 * —por ejemplo si se cerró la pestaña— vuelve a aparecer al abrir el SOA. Si hay varias, se muestran de a una.
 */
import { useEffect, useState, useCallback } from "react";
import { registrarHostEsfuerzo } from "../lib/esfuerzo.js";
import { api } from "../api/client.js";

const OPCIONES = [0.25, 0.5, 1, 2, 4, 8];
const TITULO = {
  produccion: "¿Cuánto te llevó esta pieza?",
  cambios: "¿Cuánto te llevaron los cambios?",
  revision: "¿Cuánto te llevó revisarla?",
};
const fmt = (h) => (h < 1 ? `${h * 60} min` : `${h} h`);
const clave = (p) => `${p.tarea_id}|${p.etapa}`;

export default function EsfuerzoHost() {
  const [cola, setCola] = useState([]);
  const [otro, setOtro] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const encolar = useCallback((lista) => {
    setCola((prev) => {
      const vistos = new Set(prev.map(clave));
      const nuevos = lista.filter((p) => p?.tarea_id && !vistos.has(clave(p)));
      return nuevos.length ? [...prev, ...nuevos] : prev;
    });
  }, []);

  useEffect(() => {
    registrarHostEsfuerzo(true);
    const f = (e) => { setOtro(""); encolar([e.detail]); };
    window.addEventListener("crew-pedir-esfuerzo", f);
    // Lo que quedó sin declarar de antes (otra pestaña, otro día): vuelve a pedirse.
    api.esfuerzoPendiente().then((r) => encolar(r?.pendientes || [])).catch(() => { /* sin red: se vuelve a intentar al abrir el SOA */ });
    return () => { window.removeEventListener("crew-pedir-esfuerzo", f); registrarHostEsfuerzo(false); };
  }, [encolar]);

  const pedido = cola[0];
  if (!pedido) return null;

  const guardar = async (horas) => {
    const h = Number(String(horas).replace(",", "."));
    if (!Number.isFinite(h) || h < 0.1 || h > 24) { setError("Poné entre 0,1 y 24 horas."); return; }
    setGuardando(true); setError(null);
    try {
      await api.declararEsfuerzo(pedido.tarea_id, h, pedido.etapa);
      setCola((prev) => prev.filter((p) => clave(p) !== clave(pedido)));
      setOtro("");
    } catch (e) {
      setError(e?.message || "No se pudo guardar. Probá de nuevo.");
    }
    setGuardando(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] flex justify-center p-3 sm:p-5 pointer-events-none" role="dialog" aria-label="Cuánto te llevó">
      <div className="pointer-events-auto w-full max-w-md rounded-3xl bg-card p-4 shadow-xl ring-1 ring-black/5">
        <div className="min-w-0">
          <h2 className="text-[16px] font-extrabold leading-tight">{TITULO[pedido.etapa] || TITULO.produccion}</h2>
          <p className="mt-0.5 truncate text-[12.5px] text-muted-foreground">{String(pedido.nombre || "").replace(/^\d{8}_[^_]*_/, "")}</p>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {OPCIONES.map((h) => (
            <button key={h} type="button" disabled={guardando} onClick={() => guardar(h)}
              className={`h-10 min-w-[3.5rem] rounded-full px-4 text-[14px] font-extrabold ${pedido.sugerencia_h === h ? "bg-[#0E7490] text-white" : "bg-muted text-foreground hover:bg-muted/70"} disabled:opacity-50`}>
              {fmt(h)}
            </button>
          ))}
          <form onSubmit={(e) => { e.preventDefault(); guardar(otro); }} className="flex items-center gap-1.5">
            <input value={otro} onChange={(e) => setOtro(e.target.value)} inputMode="decimal" placeholder="Otro (h)" aria-label="Otras horas"
              className="h-10 w-24 rounded-full border border-input bg-background px-3 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring/60 max-md:text-[16px]" />
            {otro.trim() && <button type="submit" disabled={guardando} className="h-10 rounded-full bg-foreground px-4 text-[14px] font-extrabold text-background disabled:opacity-50">Guardar</button>}
          </form>
        </div>
        {error && <p role="alert" className="mt-2 text-[12.5px] font-bold text-[#B91C1C]">{error}</p>}
        <p className="mt-2 text-[11.5px] text-muted-foreground">
          Es obligatorio y sirve para ajustar las horas de cada tipo de pieza.{cola.length > 1 ? ` Te quedan ${cola.length - 1} más por cargar.` : ""}
        </p>
      </div>
    </div>
  );
}
